import React, {useContext, useState} from "react";
import {Button, Text, Box, Modal, ButtonGroup, Spinner} from "@nike/eds";
import {useParams} from "react-router-dom";
import {InventoryService} from "../../shared/InventoryService";
import SnackbarContext from "../../../providers/snackbar-context";
import {edsSpace16} from "@nike/eds/dist/tokens";
import SubmitIcon from "../adjust-quantity/rocket";

enum Type {
    NEGADJ = "NEGADJ"
}

interface QuantityAdjustmentDrsModalProps {
    isOpen: boolean;
    onClose: (isConfirmed: boolean) => void;
    formState: {
        quantity: number;
        reason: string;
        comment: string;
        transportHolderNumber: string;
        billOfLading: string;
        clearGoodsIfNeeded: boolean;
    };
    inventoryService: InventoryService;
    host: string;
    location: string;
}

function QuantityAdjustmentDrsModal({
    isOpen,
    onClose,
    formState,
    inventoryService,
    host,
    location
}: QuantityAdjustmentDrsModalProps) {
    const { quantity, reason, comment, transportHolderNumber, billOfLading, clearGoodsIfNeeded } = formState;
    const params = useParams();
    const snackbarCtx = useContext(SnackbarContext);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [timestamp, setTimestamp] = useState("");
    const [processing, setProcessing] = useState(false);

    const handleSubmit = () => {
        setProcessing(true);

        inventoryService.submitManualDrsOrTransitAdjustment(
            params.materialNumber as string,
            params.size as string,
            params.countryOfOrigin as string,
            params.quality as string,
            location,
            Type.NEGADJ,
            quantity,
            reason,
            comment,
            transportHolderNumber,
            billOfLading,
            clearGoodsIfNeeded
        )
            .then((response) => {
                setProcessing(false);
                const responseData = response.data.submitManualDrsOrTransitAdjustment;
                if (responseData && responseData.timestamp != null) {
                    setTimestamp(responseData.timestamp);
                    setIsConfirmed(true);
                } else {
                    snackbarCtx.displayMsg("Failed to submit adjustment: " + response.message, "error");
                }
            })
            .catch((error) => {
                setProcessing(false);
                snackbarCtx.displayMsg("Failed to submit adjustment: " + error, "error");
            });
    };

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={() => onClose(isConfirmed)}
            headerSlot={isConfirmed ? `Adjustment ${params.materialNumber}_${params.size}_${params.countryOfOrigin}_${timestamp} sent in` : "Confirm adjustment"}
            footerSlot={
                isConfirmed ? (
                    <></>
                ) : (
                    <>
                        {processing ? (
                            <Spinner size="medium" />
                        ) : (
                            <ButtonGroup>
                                <Button variant="primary" onClick={handleSubmit}>
                                    Confirm
                                </Button>
                                <Button variant="secondary" onClick={() => onClose(false)}>
                                    Cancel
                                </Button>
                            </ButtonGroup>
                        )}
                    </>
                )
            }
        >
            <Box>
                {isConfirmed ? (
                    <>
                        <Text>
                            Please note: processing the adjustment will take some time and a refresh of the page is
                            necessary.
                        </Text>
                        <div style={{marginTop: edsSpace16}}>
                            <SubmitIcon />
                        </div>
                    </>
                ) : (
                    <>
                        <Text>Are you sure you want to make the following adjustment?</Text>
                        <br />
                        <ul style={{ listStyleType: "disc", paddingLeft: "1.5em" }}>
                            <li>Deduct {quantity} units from {location}</li>
                            <li>TransportHolder Number: {transportHolderNumber}</li>
                            <li>Bill Of Lading: {billOfLading}</li>
                            <li>Reason: {reason}</li>
                            <li>Comment: {comment}</li>
                            <li>Clear goods if needed: {clearGoodsIfNeeded ? "Yes" : "No"}</li>
                        </ul>
                    </>
                )}
            </Box>
        </Modal>
    );
}

export default QuantityAdjustmentDrsModal;